import React, { useCallback, useEffect, useState } from 'react';
import { useEdge } from '../../edge/EdgeContext';
import {
  useEdgeApi,
  EdgeRecentOrder,
  EdgeZReport,
  EdgeErrorBody,
  EdgeApiResult,
} from '../../edge/edgeApi';
import { formatCurrency } from '../../config/currency';

/**
 * EdgeCashPage — /edge/cash (staff screen, inside Layout)
 *
 * Cash-drawer operations + refunds for the dual-screen edge till. Only useful
 * in edge mode (VITE_EDGE_MODE=true); anywhere else it renders a plain
 * "till mode not active" message. Designed for a 15.6" 1920x1080 landscape
 * touchscreen: large touch targets, big numerals, tabular-nums money.
 *
 * Shift state comes from GET /edge/statusz (fetched by this page so actions
 * refresh it immediately rather than waiting for the 10s context poll).
 */

/* ---------------------------------------------------------------- helpers */

const parseAmount = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
};

const fmtTime = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/** Plain-language message for a failed call (tone matches EdgeStatusStrip). */
function errorMessage(res: EdgeApiResult<EdgeErrorBody>, noShiftHint: string): string {
  if (res.status === 0) return "Can't reach the till service — try again in a moment";
  if (res.status === 401) return 'Till session expired — restarting the till may help';
  if (res.status === 409) return res.body?.message || noShiftHint;
  return res.body?.message || "That didn't work — check the details and try again";
}

/* ----------------------------------------------------------------- styles */

const btnBase =
  'min-h-[60px] px-6 rounded-xl text-lg font-semibold transition-colors ' +
  'disabled:opacity-40 disabled:cursor-not-allowed';
const btnPrimary = `${btnBase} bg-emerald-700 text-white active:bg-emerald-800`;
const btnSecondary = `${btnBase} bg-white border-2 border-stone-300 text-stone-800 active:bg-stone-100`;
const btnDanger = `${btnBase} bg-red-700 text-white active:bg-red-800`;
const inputBig =
  'w-full min-h-[60px] px-4 text-2xl rounded-xl border-2 border-stone-300 ' +
  'tabular-nums focus:outline-none focus:border-emerald-600';
const panel = 'bg-white rounded-2xl border border-stone-200 shadow-sm p-5';
const errorText = 'text-red-800 bg-red-100 rounded-lg px-4 py-3 text-base';

/* ------------------------------------------------------------ modal shell */

const ModalShell: React.FC<{
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  busy: boolean;
}> = ({ title, children, onClose, busy }) => (
  <div
    className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 p-4"
    data-testid="edge-modal"
  >
    <div className="w-full max-w-lg bg-white rounded-2xl p-6 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-stone-900">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          aria-label="Close"
          className="min-h-[48px] min-w-[48px] rounded-xl text-2xl text-stone-500 disabled:opacity-40"
        >
          ✕
        </button>
      </div>
      {children}
    </div>
  </div>
);

/* ------------------------------------------------ paid-in / paid-out modal */

const AmountReasonModal: React.FC<{
  title: string;
  submitLabel: string;
  busy: boolean;
  error: string | null;
  onSubmit: (amount: number, reason: string) => void;
  onClose: () => void;
}> = ({ title, submitLabel, busy, error, onSubmit, onClose }) => {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const amountNum = parseAmount(amount);
  const valid = amountNum !== null && amountNum > 0 && reason.trim().length > 0;

  return (
    <ModalShell title={title} onClose={onClose} busy={busy}>
      <label className="flex flex-col gap-1 text-base font-medium text-stone-700">
        Amount
        <input
          data-testid="modal-amount"
          className={inputBig}
          inputMode="decimal"
          placeholder="0.00"
          value={amount}
          onChange={e => setAmount(e.target.value)}
        />
      </label>
      <label className="flex flex-col gap-1 text-base font-medium text-stone-700">
        Reason (required)
        <input
          data-testid="modal-reason"
          className={inputBig}
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="e.g. change from the safe"
        />
      </label>
      {error && (
        <div className={errorText} data-testid="modal-error">
          {error}
        </div>
      )}
      <button
        type="button"
        data-testid="modal-submit"
        className={btnPrimary}
        disabled={!valid || busy}
        onClick={() => valid && onSubmit(amountNum, reason.trim())}
      >
        {busy ? 'Working…' : submitLabel}
      </button>
    </ModalShell>
  );
};

/* ---------------------------------------------------------- no-sale modal */

const NoSaleModal: React.FC<{
  busy: boolean;
  error: string | null;
  onSubmit: (reason: string | undefined) => void;
  onClose: () => void;
}> = ({ busy, error, onSubmit, onClose }) => {
  const [reason, setReason] = useState('');
  return (
    <ModalShell title="Open drawer (no sale)" onClose={onClose} busy={busy}>
      <label className="flex flex-col gap-1 text-base font-medium text-stone-700">
        Reason (optional)
        <input
          data-testid="modal-reason"
          className={inputBig}
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="e.g. customer needed change"
        />
      </label>
      {error && (
        <div className={errorText} data-testid="modal-error">
          {error}
        </div>
      )}
      <button
        type="button"
        data-testid="modal-submit"
        className={btnPrimary}
        disabled={busy}
        onClick={() => onSubmit(reason.trim() === '' ? undefined : reason.trim())}
      >
        {busy ? 'Working…' : 'Open drawer'}
      </button>
    </ModalShell>
  );
};

/* ------------------------------------------------------ close-shift modal */

const CloseShiftModal: React.FC<{
  busy: boolean;
  error: string | null;
  onSubmit: (countedCash: number, notes: string | undefined) => void;
  onClose: () => void;
}> = ({ busy, error, onSubmit, onClose }) => {
  const [counted, setCounted] = useState('');
  const [notes, setNotes] = useState('');
  const countedNum = parseAmount(counted);
  const valid = countedNum !== null && countedNum >= 0;

  return (
    <ModalShell title="Close shift (Z-report)" onClose={onClose} busy={busy}>
      <p className="text-base text-stone-600">
        Count all cash in the drawer (including the float) and enter the total.
      </p>
      <label className="flex flex-col gap-1 text-base font-medium text-stone-700">
        Counted cash
        <input
          data-testid="counted-cash"
          className={inputBig}
          inputMode="decimal"
          placeholder="0.00"
          value={counted}
          onChange={e => setCounted(e.target.value)}
        />
      </label>
      <label className="flex flex-col gap-1 text-base font-medium text-stone-700">
        Notes (optional)
        <input
          data-testid="close-notes"
          className={inputBig}
          value={notes}
          onChange={e => setNotes(e.target.value)}
        />
      </label>
      {error && (
        <div className={errorText} data-testid="modal-error">
          {error}
        </div>
      )}
      <button
        type="button"
        data-testid="modal-submit"
        className={btnDanger}
        disabled={!valid || busy}
        onClick={() => valid && onSubmit(countedNum, notes.trim() === '' ? undefined : notes.trim())}
      >
        {busy ? 'Closing…' : 'Close shift'}
      </button>
    </ModalShell>
  );
};

/* ----------------------------------------------------------- refund modal */

const REFUND_TENDERS = ['CASH', 'CARD'];

const RefundModal: React.FC<{
  order: EdgeRecentOrder;
  busy: boolean;
  error: string | null;
  /** Server-corrected remaining (from a 409) — overrides the row's value. */
  serverRemaining: number | null;
  onSubmit: (amount: number, tender: string, reason: string) => void;
  onClose: () => void;
}> = ({ order, busy, error, serverRemaining, onSubmit, onClose }) => {
  const remaining = serverRemaining ?? order.remaining;
  const [amount, setAmount] = useState(String(order.remaining.toFixed(2)));
  const [tender, setTender] = useState(order.tender);
  const [reason, setReason] = useState('');
  const amountNum = parseAmount(amount);
  const valid =
    amountNum !== null && amountNum > 0 && amountNum <= remaining && reason.trim().length > 0;

  const tenders = REFUND_TENDERS.includes(order.tender)
    ? REFUND_TENDERS
    : [order.tender, ...REFUND_TENDERS];

  return (
    <ModalShell title={`Refund bill ${order.billNumber}`} onClose={onClose} busy={busy}>
      <p className="text-base text-stone-600 tabular-nums">
        Paid {formatCurrency(order.total)} by {order.tender} ·{' '}
        <span data-testid="refund-remaining">{formatCurrency(remaining)}</span> left to refund
      </p>
      <label className="flex flex-col gap-1 text-base font-medium text-stone-700">
        Amount to refund
        <input
          data-testid="refund-amount"
          className={inputBig}
          inputMode="decimal"
          value={amount}
          onChange={e => setAmount(e.target.value)}
        />
      </label>
      <div className="flex flex-col gap-1 text-base font-medium text-stone-700">
        Refund to
        <div className="flex gap-2">
          {tenders.map(t => (
            <button
              key={t}
              type="button"
              data-testid={`refund-tender-${t}`}
              onClick={() => setTender(t)}
              className={`${btnBase} flex-1 ${
                tender === t
                  ? 'bg-emerald-700 text-white'
                  : 'bg-white border-2 border-stone-300 text-stone-800'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      <label className="flex flex-col gap-1 text-base font-medium text-stone-700">
        Reason (required)
        <input
          data-testid="refund-reason"
          className={inputBig}
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="e.g. wrong item"
        />
      </label>
      {error && (
        <div className={errorText} data-testid="modal-error">
          {error}
        </div>
      )}
      <button
        type="button"
        data-testid="refund-submit"
        className={btnDanger}
        disabled={!valid || busy}
        onClick={() => valid && onSubmit(amountNum, tender, reason.trim())}
      >
        {busy ? 'Refunding…' : 'Refund'}
      </button>
    </ModalShell>
  );
};

/* --------------------------------------------------------- Z-report panel */

const varianceTone = (v: number): { cls: string; label: string } => {
  const abs = Math.abs(v);
  if (abs < 0.005) return { cls: 'text-emerald-700', label: 'Drawer balances' };
  if (abs < 5) return { cls: 'text-amber-700', label: 'Small difference — note it in the log' };
  return { cls: 'text-red-700', label: 'Large difference — tell a manager' };
};

const ZReportView: React.FC<{ report: EdgeZReport; onDone: () => void }> = ({
  report,
  onDone,
}) => {
  const tone = varianceTone(report.variance);
  const row = (label: string, value: number, testId?: string) => (
    <div className="flex items-center justify-between py-2 border-b border-stone-100 text-lg">
      <span className="text-stone-600">{label}</span>
      <span className="font-semibold tabular-nums" data-testid={testId}>
        {formatCurrency(value)}
      </span>
    </div>
  );
  return (
    <div data-testid="zreport" className="flex flex-col gap-3">
      <h2 className="text-2xl font-bold text-stone-900">Shift closed — Z-report</h2>
      <div>
        {row('Opening float', report.openingFloat, 'z-opening-float')}
        {row('Cash sales', report.cashSales, 'z-cash-sales')}
        {row('Paid in', report.paidIn, 'z-paid-in')}
        {row('Paid out', report.paidOut, 'z-paid-out')}
        {row('Expected cash', report.expectedCash, 'z-expected')}
        {row('Counted cash', report.countedCash, 'z-counted')}
      </div>
      <div className="text-center py-2">
        <div className="text-base uppercase tracking-wide text-stone-500">Variance</div>
        <div
          data-testid="z-variance"
          className={`text-5xl font-bold tabular-nums ${tone.cls}`}
        >
          {formatCurrency(report.variance)}
        </div>
        <div className={`text-base mt-1 ${tone.cls}`} data-testid="z-variance-note">
          {tone.label}
        </div>
      </div>
      <button type="button" data-testid="zreport-done" className={btnPrimary} onClick={onDone}>
        Done
      </button>
    </div>
  );
};

/* -------------------------------------------------------------- main page */

type ModalKind =
  | { kind: 'paid-in' }
  | { kind: 'paid-out' }
  | { kind: 'no-sale' }
  | { kind: 'close-shift' }
  | { kind: 'refund'; order: EdgeRecentOrder };

interface ShiftState {
  open: boolean;
  openedAt: string | null;
}

const EdgeCashPage: React.FC = () => {
  const { isEdgeMode } = useEdge();
  const api = useEdgeApi();

  const [shift, setShift] = useState<ShiftState | null>(null); // null = loading
  const [orders, setOrders] = useState<EdgeRecentOrder[]>([]);
  const [pageError, setPageError] = useState<string | null>(null);
  const [openFloat, setOpenFloat] = useState('');
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState<ModalKind | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [serverRemaining, setServerRemaining] = useState<number | null>(null);
  const [zReport, setZReport] = useState<EdgeZReport | null>(null);

  const refresh = useCallback(async () => {
    const [statusRes, ordersRes] = await Promise.all([api.statusz(), api.recentOrders(20)]);
    if (statusRes.ok && statusRes.body) {
      setShift(statusRes.body.shift ?? { open: false, openedAt: null });
      setPageError(null);
    } else {
      setPageError("Can't reach the till service — cash actions are unavailable");
    }
    if (ordersRes.ok && Array.isArray(ordersRes.body)) {
      setOrders(ordersRes.body);
    }
  }, [api]);

  useEffect(() => {
    if (!isEdgeMode) return;
    refresh();
  }, [isEdgeMode, refresh]);

  if (!isEdgeMode) {
    return (
      <div
        data-testid="edge-cash-inactive"
        className="flex items-center justify-center min-h-[50vh] p-8 text-center text-xl text-stone-600"
      >
        Till mode is not active on this device.
      </div>
    );
  }

  const closeModal = () => {
    setModal(null);
    setModalError(null);
    setServerRemaining(null);
  };

  /* -- actions -- */

  const handleOpenShift = async () => {
    const floatNum = openFloat.trim() === '' ? undefined : parseAmount(openFloat);
    if (openFloat.trim() !== '' && (floatNum === null || floatNum < 0)) return;
    setBusy(true);
    const res = await api.drawerOpen(floatNum !== undefined && floatNum !== null ? { float: floatNum } : {});
    setBusy(false);
    if (res.ok) {
      setOpenFloat('');
      setPageError(null);
      await refresh();
    } else {
      setPageError(errorMessage(res, "Couldn't open the drawer — try again"));
    }
  };

  const handlePaidInOut = async (kind: 'paid-in' | 'paid-out', amount: number, reason: string) => {
    setBusy(true);
    setModalError(null);
    const res =
      kind === 'paid-in' ? await api.paidIn({ amount, reason }) : await api.paidOut({ amount, reason });
    setBusy(false);
    if (res.ok) {
      closeModal();
      await refresh();
    } else {
      setModalError(errorMessage(res, 'No shift is open on this till — open the drawer first'));
      if (res.status === 409) await refresh();
    }
  };

  const handleNoSale = async (reason: string | undefined) => {
    setBusy(true);
    setModalError(null);
    const res = await api.noSale(reason !== undefined ? { reason } : {});
    setBusy(false);
    if (res.ok) {
      closeModal();
    } else {
      setModalError(errorMessage(res, 'No shift is open on this till — open the drawer first'));
    }
  };

  const handleCloseShift = async (countedCash: number, notes: string | undefined) => {
    setBusy(true);
    setModalError(null);
    const res = await api.shiftClose(notes !== undefined ? { countedCash, notes } : { countedCash });
    setBusy(false);
    if (res.ok && res.body) {
      closeModal();
      setZReport(res.body as EdgeZReport);
      setShift({ open: false, openedAt: null });
    } else {
      setModalError(errorMessage(res, 'No shift is open on this till'));
    }
  };

  const handleRefund = async (order: EdgeRecentOrder, amount: number, tender: string, reason: string) => {
    setBusy(true);
    setModalError(null);
    const res = await api.refund(order.orderUuid, { amount, tender, reason });
    setBusy(false);
    if (res.ok) {
      closeModal();
      await refresh();
      return;
    }
    if (res.status === 409 && res.body && typeof res.body.remaining === 'number') {
      const remaining = res.body.remaining;
      setServerRemaining(remaining);
      setOrders(prev =>
        prev.map(o => (o.orderUuid === order.orderUuid ? { ...o, remaining } : o)),
      );
      setModalError(
        res.body.message ||
          `Only ${formatCurrency(remaining)} is left to refund on this bill`,
      );
    } else {
      setModalError(errorMessage(res, "Couldn't refund this bill — try again"));
    }
  };

  /* -- render -- */

  const floatNum = parseAmount(openFloat);
  const floatValid = openFloat.trim() === '' || (floatNum !== null && floatNum >= 0);

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 grid gap-6 lg:grid-cols-2 items-start">
      {pageError && (
        <div className={`${errorText} lg:col-span-2`} data-testid="page-error" role="alert">
          {pageError}
        </div>
      )}

      {/* ----- Shift / drawer panel ----- */}
      <section className={panel} aria-label="Cash drawer">
        <h1 className="text-2xl font-bold text-stone-900 mb-4">Cash drawer</h1>

        {zReport ? (
          <ZReportView
            report={zReport}
            onDone={() => {
              setZReport(null);
              refresh();
            }}
          />
        ) : shift === null ? (
          <p className="text-lg text-stone-500" data-testid="shift-loading">
            Checking the drawer…
          </p>
        ) : !shift.open ? (
          <div className="flex flex-col gap-4" data-testid="open-shift-panel">
            <p className="text-lg text-stone-600">
              No shift is open. Put the float in the drawer and start the shift.
            </p>
            <label className="flex flex-col gap-1 text-base font-medium text-stone-700">
              Opening float
              <input
                data-testid="open-float-input"
                className={inputBig}
                inputMode="decimal"
                placeholder="0.00"
                value={openFloat}
                onChange={e => setOpenFloat(e.target.value)}
              />
            </label>
            <button
              type="button"
              data-testid="open-shift-btn"
              className={btnPrimary}
              disabled={busy || !floatValid}
              onClick={handleOpenShift}
            >
              {busy ? 'Opening…' : 'Open drawer / start shift'}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-4" data-testid="shift-open-panel">
            <p className="text-lg text-stone-600">
              Shift open since{' '}
              <span className="font-semibold tabular-nums" data-testid="shift-opened-at">
                {fmtTime(shift.openedAt)}
              </span>
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="paid-in-btn"
                className={btnSecondary}
                disabled={busy}
                onClick={() => setModal({ kind: 'paid-in' })}
              >
                Paid in
              </button>
              <button
                type="button"
                data-testid="paid-out-btn"
                className={btnSecondary}
                disabled={busy}
                onClick={() => setModal({ kind: 'paid-out' })}
              >
                Paid out
              </button>
              <button
                type="button"
                data-testid="no-sale-btn"
                className={btnSecondary}
                disabled={busy}
                onClick={() => setModal({ kind: 'no-sale' })}
              >
                No sale
              </button>
              <button
                type="button"
                data-testid="close-shift-btn"
                className={btnDanger}
                disabled={busy}
                onClick={() => setModal({ kind: 'close-shift' })}
              >
                Close shift (Z-report)
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ----- Refunds panel ----- */}
      <section className={panel} aria-label="Refunds">
        <h1 className="text-2xl font-bold text-stone-900 mb-4">Refunds</h1>
        {orders.length === 0 ? (
          <p className="text-lg text-stone-500" data-testid="no-orders">
            No recent settled orders.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-stone-100">
            {orders.map(order => {
              const fullyRefunded = order.remaining <= 0;
              const partlyRefunded = !fullyRefunded && order.remaining < order.total;
              return (
                <li key={order.orderUuid}>
                  <button
                    type="button"
                    data-testid={`order-row-${order.billNumber}`}
                    className="w-full min-h-[64px] flex items-center gap-4 px-2 py-3 text-left active:bg-stone-50 disabled:opacity-50"
                    disabled={busy || fullyRefunded}
                    onClick={() => setModal({ kind: 'refund', order })}
                  >
                    <span className="text-lg font-bold text-stone-900 tabular-nums">
                      {order.billNumber}
                    </span>
                    <span className="text-base text-stone-500 tabular-nums">
                      {fmtTime(order.settledAt)} · {order.tender}
                    </span>
                    <span className="ml-auto text-right">
                      <span className="block text-lg font-semibold tabular-nums">
                        {formatCurrency(order.total)}
                      </span>
                      {fullyRefunded ? (
                        <span
                          className="block text-sm font-semibold text-red-700"
                          data-testid={`order-refunded-${order.billNumber}`}
                        >
                          Refunded
                        </span>
                      ) : partlyRefunded ? (
                        <span className="block text-sm text-amber-700 tabular-nums">
                          {formatCurrency(order.remaining)} left to refund
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ----- Modals ----- */}
      {modal?.kind === 'paid-in' && (
        <AmountReasonModal
          title="Paid in"
          submitLabel="Add to drawer"
          busy={busy}
          error={modalError}
          onClose={closeModal}
          onSubmit={(amount, reason) => handlePaidInOut('paid-in', amount, reason)}
        />
      )}
      {modal?.kind === 'paid-out' && (
        <AmountReasonModal
          title="Paid out"
          submitLabel="Take from drawer"
          busy={busy}
          error={modalError}
          onClose={closeModal}
          onSubmit={(amount, reason) => handlePaidInOut('paid-out', amount, reason)}
        />
      )}
      {modal?.kind === 'no-sale' && (
        <NoSaleModal busy={busy} error={modalError} onClose={closeModal} onSubmit={handleNoSale} />
      )}
      {modal?.kind === 'close-shift' && (
        <CloseShiftModal
          busy={busy}
          error={modalError}
          onClose={closeModal}
          onSubmit={handleCloseShift}
        />
      )}
      {modal?.kind === 'refund' && (
        <RefundModal
          order={modal.order}
          busy={busy}
          error={modalError}
          serverRemaining={serverRemaining}
          onClose={closeModal}
          onSubmit={(amount, tender, reason) => handleRefund(modal.order, amount, tender, reason)}
        />
      )}
    </div>
  );
};

export default EdgeCashPage;
